/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import { ofType } from 'redux-observable';
import { map, mergeMap } from 'rxjs/operators';
import { Observable } from 'rxjs';

import Utils from '../../utils/utils';
import constants from '../../constants';
import type { Action } from '../../store/state/state-manager';
import { processCallBacks } from '../../case/open-local-action/helper';
import { getDataObject, dispatch } from '../../store/state/state-manager';
import { isErrorActionType, promiseHandler } from '../../case/common-helper';
import { processDataToDispatchErrorAction } from '../../store/state/epics/util';
import { postProcess, resetLoading } from '../../store/state/actions/action-creators';
import type { ActionsOut } from '../../case/types';

import { OPEN_OBJECT_ACTION } from './action-creator';
import { postProcessOpenObjectActionRequest } from './helper';
import processOpenObjectActionRequest from './async-request';

const openObjectWideAction = (action$: Observable<Action>) => {
  let actionMgrID = 0;
  let actionsOut: ActionsOut[] = [];

  return action$.pipe(
    ofType(OPEN_OBJECT_ACTION),
    map((action: Action) => {
      ({ actionMgrID } = action.payload);
      return action.payload;
    }),
    mergeMap((actionInPayload) => {
      return processOpenObjectActionRequest(actionInPayload);
    }),
    mergeMap(async (chainedData) => {
      if (isErrorActionType(chainedData.type)) {
        const errorAction = processDataToDispatchErrorAction(chainedData);
        actionsOut.push(errorAction);
        const dataObject = getDataObject(chainedData.payload.context);
        if (dataObject?.context_data?.isLoading) {
          actionsOut.push(postProcess(resetLoading, chainedData.payload));
        }
        return actionsOut;
      }

      const { actionInPayload, headers, response } = chainedData;
      const { actionID } = actionInPayload;
      /* updating activeActionID, will usefull during finishAssignment */
      Utils.setIn(response, 'data.objectInfo.activeActionID', actionID);

      /* Setting isLocalAction Flag to true after launching local action due to non availability of availableActions */
      Utils.setIn(response.data, constants.getProperty(constants.OBJECT_INFO_IS_LOCAL_ACTION), true);
      if (actionInPayload.refreshConditions) {
        Utils.setIn(
          response.data,
          constants.getProperty(constants.CASE_INFO_REFRESH_CONDITIONS),
          actionInPayload.refreshConditions
        );
      }

      await postProcessOpenObjectActionRequest(response, actionsOut, { headers, actionInPayload });
      processCallBacks(actionInPayload);

      return actionsOut;
    }),
    map(() => {
      dispatch(actionsOut);
      promiseHandler(actionsOut, actionMgrID);
      actionsOut = [];
      return actionsOut;
    })
  );
};

export default openObjectWideAction;
